// shadcn/ui component: https://ui.shadcn.com/docs/components/radix/input-group
import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/component/ds/utils';
import { Button, type ButtonProps } from 'src/component/ds/ui/button';
import { Input, type InputProps } from 'src/component/ds/ui/input';
import { Textarea } from 'src/component/ds/ui/textarea';

/*
 * Deviations from shadcn:
 * - InputGroupInput takes Input's `size` (shadcn's Input has none)
 * - InputGroupButton adds ds-min-h-0: our Button sizes by min-height, which the h-* sizes below can't override
 * - shadcn's rem margins are in px (0.45rem = 7x.2px): our root font size is 10px, not 16px
 */

function InputGroup ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot='input-group'
      role='group'
      className={cn(
        'ds-group/input-group ds-relative ds-flex ds-w-full ds-items-center ds-rounded-md ds-border ds-border-input ds-bg-background ds-shadow-xs ds-transition-[color,box-shadow] ds-outline-none',
        'ds-h-9 ds-min-w-0 has-[>textarea]:ds-h-auto',

        // Variants based on alignment.
        'has-[>[data-align=inline-start]]:[&>input]:ds-pl-2',
        'has-[>[data-align=inline-end]]:[&>input]:ds-pr-2',
        'has-[>[data-align=block-start]]:ds-h-auto has-[>[data-align=block-start]]:ds-flex-col has-[>[data-align=block-start]]:[&>input]:ds-pb-3',
        'has-[>[data-align=block-end]]:ds-h-auto has-[>[data-align=block-end]]:ds-flex-col has-[>[data-align=block-end]]:[&>input]:ds-pt-3',

        // Focus state.
        'has-[[data-slot=input-group-control]:focus-visible]:ds-border-input-focus',
        'has-[[data-slot=input-group-control]:focus-visible]:ds-shadow-chemelex-focus-field',

        // Error state.
        'has-[[data-slot][aria-invalid=true]]:ds-border-destructive',
        'has-[[data-slot][aria-invalid=true]:focus-visible]:ds-shadow-chemelex-focus-error',

        className
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva(
  "ds-flex ds-h-auto ds-cursor-text ds-items-center ds-justify-center ds-gap-2 ds-py-1.5 ds-text-sm ds-font-medium ds-text-muted-foreground ds-select-none group-data-[disabled=true]/input-group:ds-opacity-50 [&>kbd]:ds-rounded-sm [&>svg:not([class*='size-'])]:ds-size-4",
  {
    variants: {
      align: {
        'inline-start':
          'ds-order-first ds-pl-3 has-[>button]:ds-ml-[-7.2px] has-[>kbd]:ds-ml-[-5.6px]',
        'inline-end':
          'ds-order-last ds-pr-3 has-[>button]:ds-mr-[-7.2px] has-[>kbd]:ds-mr-[-5.6px]',
        'block-start':
          'ds-order-first ds-w-full ds-justify-start ds-px-3 ds-pt-3 group-has-[>input]/input-group:ds-pt-2.5 [.border-b]:ds-pb-3',
        'block-end':
          'ds-order-last ds-w-full ds-justify-start ds-px-3 ds-pb-3 group-has-[>input]/input-group:ds-pb-2.5 [.border-t]:ds-pt-3'
      }
    },
    defaultVariants: {
      align: 'inline-start'
    }
  }
);

function InputGroupAddon ({
  className,
  align = 'inline-start',
  ...props
}: React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role='group'
      data-slot='input-group-addon'
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return;
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus();
      }}
      {...props}
    />
  );
}

const inputGroupButtonVariants = cva(
  'ds-flex ds-min-h-0 ds-items-center ds-gap-2 ds-text-sm ds-shadow-none',
  {
    variants: {
      size: {
        xs: "ds-h-6 ds-gap-1 ds-rounded-[calc(var(--ds-radius)-5px)] ds-px-2 has-[>svg]:ds-px-2 [&>svg:not([class*='size-'])]:ds-size-3.5",
        sm: 'ds-h-8 ds-gap-1.5 ds-rounded-md ds-px-2.5 has-[>svg]:ds-px-2.5',
        'icon-xs':
          'ds-size-6 ds-rounded-[calc(var(--ds-radius)-5px)] ds-p-0 has-[>svg]:ds-p-0',
        'icon-sm': 'ds-size-8 ds-p-0 has-[>svg]:ds-p-0'
      }
    },
    defaultVariants: {
      size: 'xs'
    }
  }
);

export type InputGroupButtonProps = Omit<ButtonProps, 'size'> & VariantProps<typeof inputGroupButtonVariants>;

const InputGroupButton = React.forwardRef<HTMLButtonElement, InputGroupButtonProps>(
  ({ className, type = 'button', variant = 'ghost', size = 'xs', ...props }, ref) => (
    <Button
      ref={ref}
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  )
);
InputGroupButton.displayName = 'InputGroupButton';

function InputGroupText ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        "ds-flex ds-items-center ds-gap-2 ds-text-sm ds-text-muted-foreground [&_svg]:ds-pointer-events-none [&_svg:not([class*='size-'])]:ds-size-4",
        className
      )}
      {...props}
    />
  );
}

const InputGroupInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => (
    <Input
      ref={ref}
      data-slot='input-group-control'
      className={cn(
        'ds-flex-1 ds-rounded-none ds-border-0 ds-bg-transparent ds-shadow-none focus-visible:ds-shadow-none',
        className
      )}
      {...props}
    />
  )
);
InputGroupInput.displayName = 'InputGroupInput';

const InputGroupTextarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => (
    <Textarea
      ref={ref}
      data-slot='input-group-control'
      className={cn(
        'ds-flex-1 ds-resize-none ds-rounded-none ds-border-0 ds-bg-transparent ds-py-3 ds-shadow-none focus-visible:ds-shadow-none',
        className
      )}
      {...props}
    />
  )
);
InputGroupTextarea.displayName = 'InputGroupTextarea';

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea
};
